import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CashFlowEntry } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { PeriodNavigator } from "../../components/PeriodNavigator";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateInput } from "../../utils/dateRange";
import {
  CASHFLOW_ORIGIN_PT,
  CASHFLOW_SOURCE_TYPE_PT,
  CASHFLOW_TYPE_PT,
} from "../../constants/cashflowPt";

export const CashFlowPage: React.FC = () => {
  const currentRange = useMemo(() => getCurrentMonthRange(), []);
  const [fromDate, setFromDate] = useState(currentRange.from);
  const [toDate, setToDate] = useState(currentRange.to);
  const [typeFilter, setTypeFilter] = useState("ALL");

  const listFilters = useMemo(
    () => ({
      ...(typeFilter !== "ALL" ? { type: typeFilter } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      ...(toDate ? { to: toDate } : {}),
    }),
    [fromDate, toDate, typeFilter]
  );

  const handlePeriodChange = useCallback((from: string, to: string) => {
    setFromDate(from ? formatDateInput(new Date(from)) : from);
    setToDate(to ? formatDateInput(new Date(to)) : to);
  }, []);


  const toDateInput = useCallback((value: string | number | boolean | null) => {
    if (!value || typeof value !== "string") {
      return "";
    }
    return value.length >= 10 ? value.slice(0, 10) : value;
  }, []);

  const CashFlowFormExtras: React.FC<{
    values: Record<string, string | number | boolean | null>;
    setValues: React.Dispatch<
      React.SetStateAction<Record<string, string | number | boolean | null>>
    >;
    editingId: number | null;
  }> = ({ values, setValues, editingId }) => {
    useEffect(() => {
      if (!editingId) {
        return;
      }
      const normalized = toDateInput(values.referenceDate);
      if (normalized && normalized !== values.referenceDate) {
        setValues((prev) => ({ ...prev, referenceDate: normalized }));
      }
    }, [editingId, setValues, toDateInput, values.referenceDate]);

    return null;
  };

  return (
    <CrudPage<CashFlowEntry>
      title="Fluxo de caixa"
      endpoint="/modules/cashflow"
      searchPlaceholder="Buscar por descrição..."
      listFilters={listFilters}
      renderFilters={
        <div
          className="d-flex flex-wrap align-items-end gap-3 px-3 py-2 mb-2 rounded"
          style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
        >
          <small className="text-muted fw-semibold me-1 align-self-center">Filtros</small>
          <div>
            <label className="form-label mb-1 small fw-semibold">Tipo</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 140 }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="ALL">Todos</option>
              <option value="PLANNED">Planejado</option>
              <option value="ACTUAL">Realizado</option>
            </select>
          </div>
          <div>
            <label className="form-label mb-1 small fw-semibold">De</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150 }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label mb-1 small fw-semibold">Até</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150 }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="align-self-end">
            <PeriodNavigator fromDate={fromDate} toDate={toDate} onChange={handlePeriodChange} />
          </div>
        </div>
      }
      columns={[
        { label: "Descrição", render: (item) => item.description },
        {
          label: "Tipo",
          render: (item) => {
            const label = CASHFLOW_TYPE_PT[item.type] ?? item.type;
            const color = item.type === "ACTUAL" ? "text-bg-primary" : "text-bg-secondary";
            return <span className={`badge ${color}`} style={{ fontSize: "0.7rem" }}>{label}</span>;
          },
        },
        {
          label: "Origem",
          render: (item) => (
            <span className="text-muted small">{CASHFLOW_ORIGIN_PT[item.origin] ?? item.origin}</span>
          ),
        },
        { label: "Data", render: (item) => formatDateBR(item.referenceDate) },
        {
          label: "Valor",
          render: (item) => <span className="fw-semibold">{formatMoneyBR(item.amount)}</span>,
        },
      ]}
      formFields={[
        {
          name: "type",
          label: "Tipo",
          type: "select",
          options: [
            { value: "PLANNED", label: CASHFLOW_TYPE_PT.PLANNED },
            { value: "ACTUAL", label: CASHFLOW_TYPE_PT.ACTUAL },
          ],
          displayValue: (value) =>
            CASHFLOW_TYPE_PT[String(value ?? "")] ?? String(value ?? "-"),
        },
        {
          name: "origin",
          label: "Origem",
          type: "text",
          displayValue: (value) =>
            CASHFLOW_ORIGIN_PT[String(value ?? "")] ?? String(value ?? "-"),
        },
        {
          name: "sourceType",
          label: "Tipo de fonte",
          type: "text",
          displayValue: (value) =>
            CASHFLOW_SOURCE_TYPE_PT[String(value ?? "")] ?? String(value ?? "-"),
        },
        { name: "sourceId", label: "Fonte ID", type: "text" },
        {
          name: "referenceDate",
          label: "Data",
          type: "date",
          required: true,
          displayValue: (value) => formatDateBR(String(value ?? "")),
        },
        {
          name: "description",
          label: "Descrição",
          type: "text",
          required: true,
        },
        {
          name: "amount",
          label: "Valor",
          type: "text",
          required: true,
          displayValue: (value) => formatMoneyBR(Number(value ?? 0)),
        },
      ]}
      initialValues={{
        type: "PLANNED",
        origin: "MANUAL",
        sourceType: "",
        sourceId: "",
        referenceDate: "",
        description: "",
        amount: "",
      }}
      mapToPayload={(values) => ({
        type: values.type,
        origin: values.origin || "MANUAL",
        sourceType: values.sourceType || null,
        sourceId: values.sourceId || null,
        referenceDate: values.referenceDate,
        description: values.description,
        amount: Number(values.amount),
      })}
      renderFormExtras={({ values, setValues, editingId }) => (
        <CashFlowFormExtras
          values={values}
          setValues={setValues}
          editingId={editingId}
        />
      )}
      permissionWrite="CASH_FLOW_WRITE"
    />
  );
};
